/**
 * Image Management Service
 * Handles image upload, optimization, storage, and management for documents
 */

class ImageService {
  constructor() {
    this.maxImageSize = 10 * 1024 * 1024; // 10MB
    this.supportedFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    this.imageCache = new Map(); // In-memory cache for optimization
  }

  /**
   * Upload and process an image file
   * @param {File} file - The image file to process
   * @param {Object} options - Processing options
   * @returns {Promise<Object>} Processed image data
   */
  async uploadImage(file, options = {}) {
    try {
      // Validate file
      this.validateImageFile(file);
      
      // Generate unique ID for the image
      const imageId = this.generateImageId();
      
      // Process the image
      const processedImage = await this.processImage(file, {
        maxWidth: options.maxWidth || 1920,
        maxHeight: options.maxHeight || 1080,
        quality: options.quality || 0.8,
        ...options
      });

      // Convert to base64 for storage
      const base64Data = await this.fileToBase64(processedImage.blob);
      
      const imageData = {
        id: imageId,
        name: file.name,
        originalName: file.name,
        size: processedImage.blob.size,
        originalSize: file.size,
        mimeType: processedImage.mimeType,
        width: processedImage.width,
        height: processedImage.height,
        base64: base64Data,
        dataUrl: `data:${processedImage.mimeType};base64,${base64Data}`,
        uploadedAt: new Date().toISOString(),
        processed: processedImage.size !== file.size
      };

      // Cache the image
      this.imageCache.set(imageId, imageData);
      
      return imageData;
    } catch (error) {
      console.error('Image upload failed:', error);
      throw new Error(`Image upload failed: ${error.message}`);
    }
  }

  /**
   * Process and optimize an image
   * @param {File} file - Original image file
   * @param {Object} options - Processing options
   * @returns {Promise<Object>} Processed image data
   */
  async processImage(file, options) {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        try {
          // Calculate new dimensions
          const { width: newWidth, height: newHeight } = this.calculateDimensions(
            img.width, 
            img.height, 
            options.maxWidth, 
            options.maxHeight
          );

          // Set canvas dimensions
          canvas.width = newWidth;
          canvas.height = newHeight;

          // Draw and compress image
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, newWidth, newHeight);

          // Convert to blob
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Image processing failed'));
                return;
              }

              resolve({
                blob,
                width: newWidth,
                height: newHeight,
                mimeType: blob.type,
                size: blob.size
              });
            },
            options.outputFormat || file.type,
            options.quality || 0.8
          );
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * Calculate optimal dimensions while maintaining aspect ratio
   */
  calculateDimensions(originalWidth, originalHeight, maxWidth, maxHeight) {
    let { width, height } = { width: originalWidth, height: originalHeight };

    // Scale down if necessary
    if (width > maxWidth) {
      height = (height * maxWidth) / width;
      width = maxWidth;
    }

    if (height > maxHeight) {
      width = (width * maxHeight) / height;
      height = maxHeight;
    }

    return { width: Math.round(width), height: Math.round(height) };
  }

  /**
   * Convert file to base64 string
   */
  async fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // Remove data URL prefix to get pure base64
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Get image from cache or storage
   */
  getImage(imageId) {
    return this.imageCache.get(imageId) || null;
  }

  /**
   * Get all cached images
   */
  getAllImages() {
    return Array.from(this.imageCache.values());
  }

  /**
   * Replace Base64 images in HTML content with aliases for AI processing
   * @param {string} htmlContent - HTML content with Base64 images
   * @returns {Object} { aliasedHtml, imageMap }
   */
  aliasImagesInContent(htmlContent) {
    if (!htmlContent) return { aliasedHtml: htmlContent, imageMap: {} };

    const imageMap = {};
    let aliasedHtml = htmlContent;
    let aliasCounter = 1;

    // Find all images with data URLs
    const imageRegex = /<img[^>]+src="data:([^"]+)"[^>]*>/g;
    let match;

    while ((match = imageRegex.exec(htmlContent)) !== null) {
      const fullMatch = match[0];
      const dataUrl = match[1];
      
      // Generate alias
      const alias = `{{IMAGE_${aliasCounter}}}`;
      
      // Store the mapping
      imageMap[alias] = {
        originalTag: fullMatch,
        dataUrl: `data:${dataUrl}`,
        aliasId: `IMAGE_${aliasCounter}`
      };
      
      // Replace in content with alias
      aliasedHtml = aliasedHtml.replace(fullMatch, `<img src="${alias}" data-image-alias="${alias}" />`);
      
      aliasCounter++;
    }

    return { aliasedHtml, imageMap };
  }

  /**
   * Restore aliases back to Base64 images in HTML content
   * @param {string} aliasedHtml - HTML content with image aliases
   * @param {Object} imageMap - Mapping of aliases to original image data
   * @returns {string} HTML content with restored Base64 images
   */
  restoreImagesFromAliases(aliasedHtml, imageMap) {
    if (!aliasedHtml || !imageMap) return aliasedHtml;

    let restoredHtml = aliasedHtml;

    // Restore each aliased image
    Object.keys(imageMap).forEach(alias => {
      const imageData = imageMap[alias];
      
      // Replace alias with original image tag
      const aliasRegex = new RegExp(`<img[^>]*src="${alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"[^>]*>`, 'g');
      restoredHtml = restoredHtml.replace(aliasRegex, imageData.originalTag);
    });

    return restoredHtml;
  }

  /**
   * Extract image information for AI context (without Base64 data)
   * @param {Object} imageMap - Image mapping from aliasing
   * @returns {Object} Image context for AI
   */
  createImageContextForAI(imageMap) {
    const imageContext = {};
    
    Object.keys(imageMap).forEach(alias => {
      const imageData = imageMap[alias];
      
      // Extract image attributes without the data
      const imgElement = document.createElement('div');
      imgElement.innerHTML = imageData.originalTag;
      const img = imgElement.querySelector('img');
      
      imageContext[alias] = {
        type: 'image',
        alt: img.getAttribute('alt') || 'Image',
        title: img.getAttribute('title') || '',
        width: img.getAttribute('width') || 'auto',
        height: img.getAttribute('height') || 'auto',
        className: img.getAttribute('class') || ''
      };
    });

    return imageContext;
  }

  /**
   * Debug helper - analyze image content in HTML
   */
  debugImageContent(htmlContent) {
    const imgTags = htmlContent?.match(/<img[^>]*>/g) || [];
    
    imgTags.forEach((tag, index) => {
      const srcMatch = tag.match(/src="([^"]+)"/);
      if (srcMatch) {
        const src = srcMatch[1];
        if (src.startsWith('data:image/')) {
          // Base64 image detected (no-op in debug)
        }
      }
    });
    
    return {
      totalLength: htmlContent?.length || 0,
      imageCount: imgTags.length,
      containsImages: htmlContent?.includes('data:image/') || false
    };
  }

  /**
   * Check if Base64 string appears complete
   */
  isBase64Complete(base64String) {
    if (!base64String) return false;
    if (base64String.length < 100) return false;
    
    // Check if it ends properly (Base64 should end with padding or valid characters)
    const validEnding = /[A-Za-z0-9+/]={0,2}$/.test(base64String);
    const hasValidChars = /^[A-Za-z0-9+/=]+$/.test(base64String);
    
    return validEnding && hasValidChars;
  }

  /**
   * Delete an image
   */
  deleteImage(imageId) {
    this.imageCache.delete(imageId);
    
    // In a real app, you'd also delete from persistent storage
    // await this.deleteFromStorage(imageId);
    
    return true;
  }

  /**
   * Validate image file
   */
  validateImageFile(file) {
    if (!file) {
      throw new Error('No file provided');
    }

    if (!this.supportedFormats.includes(file.type)) {
      throw new Error(`Unsupported image format. Supported formats: ${this.supportedFormats.join(', ')}`);
    }

    if (file.size > this.maxImageSize) {
      throw new Error(`Image size too large. Maximum size: ${this.maxImageSize / (1024 * 1024)}MB`);
    }
  }

  /**
   * Generate unique image ID
   */
  generateImageId() {
    return `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Extract images from HTML content
   */
  extractImagesFromHtml(htmlContent) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const images = doc.querySelectorAll('img');
    
    return Array.from(images).map(img => ({
      src: img.src,
      alt: img.alt || '',
      width: img.width || null,
      height: img.height || null,
      style: img.style.cssText || '',
      className: img.className || ''
    }));
  }

  /**
   * Replace image URLs in HTML with base64 data URLs
   */
  embedImagesInHtml(htmlContent) {
    let updatedHtml = htmlContent;
    
    this.imageCache.forEach((imageData) => {
      // Replace any references to this image with the data URL
      const imageRegex = new RegExp(`src="[^"]*${imageData.id}[^"]*"`, 'g');
      updatedHtml = updatedHtml.replace(imageRegex, `src="${imageData.dataUrl}"`);
    });
    
    return updatedHtml;
  }

  /**
   * Prepare images for export
   */
  prepareImagesForExport(htmlContent, format = 'docx') {
    const images = this.extractImagesFromHtml(htmlContent);
    const preparedImages = [];

    images.forEach(img => {
      if (img.src.startsWith('data:')) {
        // Extract base64 data
        const matches = img.src.match(/^data:([^;]+);base64,(.+)$/);
        if (matches) {
          preparedImages.push({
            ...img,
            mimeType: matches[1],
            base64: matches[2],
            buffer: this.base64ToBuffer(matches[2])
          });
        }
      }
    });

    return preparedImages;
  }

  /**
   * Convert base64 to buffer for export
   */
  base64ToBuffer(base64) {
    try {
      const binaryString = atob(base64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return bytes.buffer;
    } catch (error) {
      console.error('Failed to convert base64 to buffer:', error);
      return null;
    }
  }

  /**
   * Get image dimensions from data URL
   */
  async getImageDimensions(dataUrl) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = reject;
      img.src = dataUrl;
    });
  }

  /**
   * Create thumbnail for an image
   */
  async createThumbnail(imageData, maxSize = 150) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    return new Promise((resolve) => {
      img.onload = () => {
        const { width, height } = this.calculateDimensions(
          img.width, 
          img.height, 
          maxSize, 
          maxSize
        );

        canvas.width = width;
        canvas.height = height;
        
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob((blob) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        }, 'image/jpeg', 0.7);
      };
      
      img.src = imageData.dataUrl;
    });
  }

  /**
   * Clear all cached images (useful for memory management)
   */
  clearCache() {
    this.imageCache.clear();
  }

  /**
   * Get cache size information
   */
  getCacheInfo() {
    const images = Array.from(this.imageCache.values());
    const totalSize = images.reduce((sum, img) => sum + img.size, 0);
    
    return {
      imageCount: images.length,
      totalSize,
      formattedSize: this.formatFileSize(totalSize)
    };
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}

const imageService = new ImageService();
export default imageService;
